import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditTaskListTemplateModal from '@modals/CreateOrEditTaskListTemplateModal'

import type { TaskListTemplateModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditTaskListTemplateModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditTaskListTemplateModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
// @ts-expect-error TS2304
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditTaskListTemplateModal: {
      closeModal: () => setState({ showCreateOrEditTaskListTemplateModal: false }),
      createTaskListTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditTaskListTemplateModal } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditTaskListTemplateModal: (payload: { taskListTemplate: TaskListTemplateModel }) => {
        setModalData('CreateOrEditTaskListTemplateModal', payload)
        setState({ showCreateOrEditTaskListTemplateModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditTaskListTemplateModal
          callbacks={callbacks('CreateOrEditTaskListTemplateModal', setState)}
          showModal={showCreateOrEditTaskListTemplateModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
